import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import MatchPerformanceGrid from "@/components/general/Leaderboards/MatchPerformanceGrid";
import TournamentPerformanceGrid from "@/components/general/Leaderboards/TournamentPerformanceGrid";
import SeasonPerformanceGrid from "@/components/general/Leaderboards/SeasonPerformanceGrid";
import { LeaderboardsHeader } from "@/components/general/Leaderboards/LeaderboardsHeader";
import LeaderboardsNavigation from "@/components/general/Leaderboards/LeaderboardsNavigation";
import { tournamentsQueryOptions } from "@/api/tournaments";
import {
    topMatchPerformancesQueryOptions,
    topTournamentPerformancesQueryOptions,
    topSeasonPerformancesQueryOptions,
} from "@/api/kpis";
import { useSelectedMatch } from "@/context/matchContext";
import { useSelectedTournament } from "@/context/tournamentContext";
import { useSelectedTeam } from "@/context/teamContext";

const LeaderboardsPage = () => {
    const { matchIds: selectedMatches, setMatchIds: setSelectedMatches } = useSelectedMatch();
    const { tournamentId: globalTournamentId, setTournamentId: setGlobalTournamentId } = useSelectedTournament();
    const [activeTab, setActiveTab] = useState<"match" | "tournament" | "season">("match");
    const [localSelectedTournament, setLocalSelectedTournament] = useState<number | undefined>(undefined);
    const [selectedSeason, setSelectedSeason] = useState<number>(new Date().getFullYear());

    const selectedTournament = localSelectedTournament !== undefined ? localSelectedTournament : globalTournamentId;

    const { teamId } = useSelectedTeam();
    const { data: tournaments, isLoading: tournamentsLoading } = useQuery(tournamentsQueryOptions(teamId));

    useEffect(() => {
        if (globalTournamentId !== undefined && localSelectedTournament === undefined) {
            setLocalSelectedTournament(globalTournamentId);
        }
    }, [globalTournamentId, localSelectedTournament]);

    const handleTournamentChange = (id: number | undefined) => {
        setLocalSelectedTournament(id);
        setGlobalTournamentId(id);
    };
    const { isLoading: matchPerformancesLoading } = useQuery({
        ...topMatchPerformancesQueryOptions(selectedMatches.length > 0 ? selectedMatches : undefined, teamId),
        enabled: activeTab === "match",
    });

    const { isLoading: tournamentPerformancesLoading } = useQuery({
        ...topTournamentPerformancesQueryOptions(selectedTournament, teamId),
        enabled: activeTab === "tournament",
    });

    const { isLoading: seasonPerformancesLoading } = useQuery({
        ...topSeasonPerformancesQueryOptions(selectedSeason, teamId),
        enabled: activeTab === "season",
    });

    const isLoading =
        tournamentsLoading ||
        (activeTab === "match" && matchPerformancesLoading) ||
        (activeTab === "tournament" && tournamentPerformancesLoading) ||
        (activeTab === "season" && seasonPerformancesLoading);

    return (
        <main data-leaderboards-loaded={!isLoading ? "true" : undefined} className={`flex flex-col p-4`}>
            <LeaderboardsHeader />

            <LeaderboardsNavigation
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                selectedTournament={selectedTournament}
                setSelectedTournament={handleTournamentChange}
                tournaments={tournaments}
                selectedMatches={selectedMatches}
                setSelectedMatches={setSelectedMatches}
                selectedSeason={selectedSeason}
                setSelectedSeason={setSelectedSeason}
            />

            {activeTab === "match" ? (
                <div id="onboarding-leaderboards-table">
                    <MatchPerformanceGrid selectedMatches={selectedMatches} />
                </div>
            ) : activeTab === "tournament" ? (
                <div id="onboarding-leaderboards-table">
                    <TournamentPerformanceGrid selectedTournament={selectedTournament} />
                </div>
            ) : (
                <div id="onboarding-leaderboards-table">
                    <SeasonPerformanceGrid selectedSeason={selectedSeason} />
                </div>
            )}
            <div className="lg:hidden py-7.5" />
        </main>
    );
};

export default LeaderboardsPage;
